import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../api";
import { toasts } from "../components/AppToast";
import Layout from "../components/Layout";
import Pagination from "../components/Pagination";

import { FiTrash2, FiEdit, FiPlus, FiSearch } from "react-icons/fi";
export default function ManageBrand() {
  const navigate = useNavigate();
  // Table state
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Delete modal state
  const [delModal, setDelModal] = useState(null);
  const [delSaving, setDelSaving] = useState(false);

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login");
    }
  }, [navigate]);

  // Fetch brand data
  const fetchData = useCallback(async (pg, q) => {
    setLoading(true);

    try {
      const res = await API.get("/brand", {
        params: {
          page: pg,
          limit: 10,
          search: q,
        },
      });

      setData(res.data?.data || []);
      setTotalPages(Number(res.data?.totalPages || 1));
      setTotal(Number(res.data?.total || 0));
    } catch (error) {
      console.error("Fetch brand error:", error);

      toasts.error(
        error.response?.data?.error || "Failed to load Brands"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data when page or search changes
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData(page, search);
    }, 350);

    return () => clearTimeout(timer);
  }, [page, search, fetchData]);

  // Add or update brand
  const handleSubmit = async () => {
    if (!name.trim()) {
      toasts.warning("Enter Brand name");
      return;
    }

    setSaving(true);

    try {
      if (editId) {
        // Update brand
        await API.put(`/brand/${editId}`, {
          name: name.trim(),
        });

        toasts.success("Brand updated successfully");
      } else {
        // Add brand
        await API.post("/brand", {
          name: name.trim(),
        });

        toasts.success("Brand added successfully");
      }

      // Reset form
      setName("");
      setEditId(null);

      // Reload table
      await fetchData(page, search);
    } catch (error) {
      console.error("Brand submit error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Operation failed"
      );
    } finally {
      setSaving(false);
    }
  };

  // Edit brand
  const handleEdit = (row) => {
    setName(row.mstmod_val);
    setEditId(row.mstmod_id);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Cancel edit
  const handleCancel = () => {
    setName("");
    setEditId(null);
  };

  // Open delete modal
  const openDeleteModal = (row) => {
    setDelModal(row);
  };

  // Close delete modal
  const closeDeleteModal = () => {
    if (delSaving) {
      return;
    }

    setDelModal(null);
  };

  // Delete brand
  const handleDelete = async () => {
    if (!delModal) {
      return;
    }

    setDelSaving(true);

    try {
      const deletedId = delModal.mstmod_id;

      await API.delete(`/brand/${deletedId}`);

      toasts.success("Brand deleted successfully");

      // Close delete modal
      setDelModal(null);

      // Calculate remaining records
      const remainingRecords = Math.max(0, total - 1);

      const newTotalPages = Math.max(
        1,
        Math.ceil(remainingRecords / 10)
      );

      // Move to previous page if current page becomes empty
      if (page > 1 && page > newTotalPages) {
        setPage((p) => Math.max(1, p - 1));
      } else {
        await fetchData(page, search);
      }

      // Clear edit form if deleted brand was being edited
      if (editId === deletedId) {
        setName("");
        setEditId(null);
      }
    } catch (error) {
      console.error("Delete brand error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Delete failed"
      );
    } finally {
      setDelSaving(false);
    }
  };
// =========================================================
  // PAGINATION - PREVIOUS
  // =========================================================

  const handlePrevious = () => {
    if (
      loading ||
      page <= 1
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.max(
          1,
          prev - 1
        )
    );
  };

  // =========================================================
  // PAGINATION - NEXT
  // =========================================================

  const handleNext = () => {
    if (
      loading ||
      totalPages <= 0 ||
      page >= totalPages
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.min(
          totalPages,
          prev + 1
        )
    );
  };

  return (
    <Layout activePage="Brand Details">
      <main className="dashboard-main">
        <div className="season-card">
          {/* Top controls */}

          <div className="season-top-row">
            {/* Brand form */}

            <div className="season-form-row">
              {/* Editing badge */}

              {editId && (
                <span className="season-editing-badge">
                  Editing #{editId}
                </span>
              )}

              {/* Brand name */}

              <input
                type="text"
                placeholder="Enter Brand Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !saving) {
                    handleSubmit();
                  }
                }}
                className="season-input"
              />

              {/* Cancel edit */}

              {editId && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="season-cancel-btn"
                  disabled={saving}
                >
                  Cancel
                </button>
              )}

              {/* Add or update button */}

              <button
                type="button"
                onClick={handleSubmit}
                className="um-btn-primary"
                disabled={saving}
              >
                {saving ? (
                  <span className="spinner" />
                ) : editId ? (
                  <>
                    <FiEdit />
                    Update Brand Name
                  </>
                ) : (
                  <>
                    <FiPlus />
                    Add Brand Name
                  </>
                )}
              </button>
            </div>

            {/* Search */}

            <div className="search-wrapper">
              <span className="search-icon">
                <FiSearch size={17} />
              </span>

              <input
                type="text"
                className="search-input"
                placeholder="Search Brand..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />

              {search && (
                <button
                  type="button"
                  className="season-clear-btn"
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Brand table */}

          <div className="table-responsive">
            <table className="table table-striped table-hover table-bordered align-middle mb-0">
              {/* Table header */}

              <thead className="table-light">
                <tr className="um-table-head">
                  <th className="um-th text-nowrap">#</th>

                  <th className="um-th text-nowrap text-center">
                    Brand Name
                  </th>

                  <th className="um-th text-nowrap text-center">
                    Action
                  </th>
                </tr>
              </thead>

              {/* Table body */}

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="um-td text-center"
                    >
                      Loading…
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="empty-table-cell text-center py-5"
                    >
                      {search
                        ? "No matching Brands found"
                        : "No Brands found"}
                    </td>
                  </tr>
                ) : (
                  data.map((row, i) => {
                    const isEditing =
                      editId === row.mstmod_id;

                    return (
                      <tr
                        key={row.mstmod_id}
                        className={`um-row ${
                          isEditing ? "editing-row" : ""
                        }`}
                      >
                        {/* Row number */}

                        <td className="um-td user-number text-center">
                          {(page - 1) * 10 + i + 1}
                        </td>

                        {/* Brand name */}

                        <td className="um-td text-center">
                          <span className="season-name-chip">
                            {row.mstmod_val}
                          </span>
                        </td>

                        {/* Actions */}

                        <td className="um-td text-center season-action-td">
                          <div className="action-buttons d-flex justify-content-center align-items-center gap-2">
                            {/* Edit */}

                            <button
                              type="button"
                              onClick={() => handleEdit(row)}
                              className="action-btn action-edit btn btn-sm"
                              title="Edit"
                            >
                              <FiEdit size={15} />
                            </button>

                            {/* Delete */}

                            <button
                              type="button"
                              onClick={() =>
                                openDeleteModal(row)
                              }
                              className="action-btn action-delete btn btn-sm"
                              title="Delete"
                            >
                              <FiTrash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {/* Pagination */}

            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={10}
              loading={loading}
              onPrevious={handlePrevious}
              onNext={handleNext}
            />
          </div>
        </div>

        {/* Delete modal */}

        {delModal && (
          <div
            className="um-overlay"
            onClick={closeDeleteModal}
          >
            <div
              className="um-modal um-delete-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="delete-content">
                {/* Delete icon */}

                <div className="delete-icon">
                  <FiTrash2 size={32} />
                </div>

                {/* Delete title */}

                <h2 className="delete-title">
                  Delete Brand?
                </h2>

                {/* Delete description */}

                <p className="delete-description">
                  Are you sure you want to delete{" "}
                  <span className="badge badge-success">
                    "{delModal.mstmod_val}"
                  </span>
                  ?
                  <br />
                  This will permanently remove this brand.
                  <br />
                  <strong>
                    This action cannot be undone.
                  </strong>
                </p>

                {/* Delete actions */}

                <div className="delete-actions">
                  {/* Cancel */}

                  <button
                    type="button"
                    className="um-btn-secondary"
                    onClick={closeDeleteModal}
                    disabled={delSaving}
                  >
                    Cancel
                  </button>

                  {/* Confirm delete */}

                  <button
                    type="button"
                    disabled={delSaving}
                    className="delete-confirm-btn"
                    onClick={handleDelete}
                  >
                    {delSaving ? (
                      <span className="spinner" />
                    ) : (
                      <>
                        <FiTrash2 />
                        Delete Permanently
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </main>
    </Layout>
  );
}
